import { useTranslation } from "react-i18next";
import React from "react";
import { ChevronLeft, ChevronRight, SearchIcon } from "lucide-react";

import { cn } from "@/utils/cn";
import FilterButton from "@/components/table/FilterButton";

interface Breadcrumb {
  label: string;
  onClick?: () => void;
}

interface SettingsInternalPageHeaderProps {
  title: string;
  /** Optional info affordance rendered next to the title (e.g. an InfoPopover). */
  titleInfo?: React.ReactNode;
  description?: string;
  isUpsert?: boolean;
  breadcrumbs?: Breadcrumb[];
  onBack?: () => void;
  searchValue?: string;
  onSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  searchPlaceholder?: string;
  filtersCount?: number;
  onFiltersClick?: () => void;
  onFiltersReset?: () => void;
  actions?: React.ReactNode;
  className?: string;
}

export const SettingsInternalPageHeader = ({
  title,
  titleInfo,
  description,
  isUpsert,
  breadcrumbs,
  onBack,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  filtersCount = 0,
  onFiltersClick,
  onFiltersReset,
  actions,
  className,
}: SettingsInternalPageHeaderProps) => {
  const { t } = useTranslation(["common"]);
  const showSearch = !isUpsert && !!(onSearchChange || onFiltersClick);
  // NOTE: `actions` is NOT hidden on `isUpsert`, unlike the search box. Roughly
  // a third of the call-sites swap in the form's own Save / Reset / Publish
  // buttons there (Departments, Branches, Positions, Shift, Leave Policy, …),
  // so blanket-hiding it strips the only way to submit those forms. A call-site
  // whose `actions` is a list-only "Add X" CTA must gate it itself.
  const showActions = !!actions;
  const showToolbar = showSearch || showActions;
  const showBreadcrumbs = isUpsert && !!breadcrumbs?.length;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-3 px-4 py-3 bg-muted rounded-xs ring-1 ring-border",
        className,
      )}
    >
      {/* Back button */}
      {isUpsert && onBack && (
        <button
          type="button"
          aria-label={t("common:buttons.back")}
          onClick={onBack}
          className="shrink-0 flex items-center justify-center size-9 rounded-xs border border-border text-gray-500 hover:text-primary hover:border-primary hover:bg-primary/5 transition-all"
        >
          {/* "Back" points toward the start of reading order — mirror it in
              RTL rather than leaving a left-pointing chevron in Arabic. */}
          <ChevronLeft className="size-[18px] rtl:rotate-180" />
        </button>
      )}

      {/* Title / breadcrumbs + description */}
      <div className="flex flex-col min-w-0 flex-1">
        {showBreadcrumbs ? (
          <div className="flex items-center gap-1 flex-wrap min-w-0">
            {breadcrumbs!.map((crumb, i) => (
              <React.Fragment key={i}>
                {crumb.onClick ? (
                  <button
                    type="button"
                    onClick={crumb.onClick}
                    className="text-xs text-gray-400 hover:text-primary transition-colors shrink-0"
                  >
                    {crumb.label}
                  </button>
                ) : (
                  <span className="text-xs text-gray-400 shrink-0">
                    {crumb.label}
                  </span>
                )}
                <ChevronRight className="size-3 text-gray-300 shrink-0 rtl:rotate-180" />
              </React.Fragment>
            ))}
            <span
              title={title}
              className="text-sm font-semibold text-gray-700 tracking-tight truncate"
            >
              {title}
            </span>
            {/* Also honoured on the breadcrumb path, so a detail view can badge
                its record's identity (branch type, country currency) without
                giving up its trail. */}
            {titleInfo}
          </div>
        ) : (
          <div className="flex items-center gap-1.5 min-w-0">
            <h1
              title={title}
              className="text-sm md:text-base font-semibold text-gray-700 tracking-tight truncate"
            >
              {title}
            </h1>
            {titleInfo}
          </div>
        )}
        {description && (
          <p
            title={description}
            className="text-xs text-gray-400 mt-0.5 truncate"
          >
            {description}
          </p>
        )}
      </div>

      {/* Toolbar */}
      {showToolbar && (
        <div className="flex items-center gap-2 shrink-0">
          {showSearch && onSearchChange && (
            <div className="relative flex items-center">
              <SearchIcon className="absolute start-3 top-1/2 -translate-y-1/2 size-3.5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={searchValue}
                onChange={onSearchChange}
                placeholder={searchPlaceholder}
                className="ps-8 pe-4 h-9 w-36 sm:w-48 lg:w-60 text-sm rounded-xs border border-border bg-gray-50 focus:bg-white focus:outline-hidden focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all placeholder:text-gray-400"
              />
            </div>
          )}

          {showSearch && onFiltersClick && (
            <FilterButton
              onClick={onFiltersClick}
              filtersCount={filtersCount}
              onReset={onFiltersReset}
              className="h-9"
            />
          )}

          {showActions && showSearch && (
            <div className="h-6 w-px bg-border" />
          )}

          {showActions && (
            <div className="flex items-center gap-2">{actions}</div>
          )}
        </div>
      )}
    </div>
  );
};
